import { CircleAlert, FileText, Forward, KeyRound, Megaphone, Send, type LucideIcon } from 'lucide-react';
import { useId, useState, type CSSProperties, type KeyboardEvent } from 'react';
import { toAppError } from '../../api/errors';
import { useHandOff } from '../../data/queries';
import type { Employee, Item } from '../../domain/models';
import { useLocale, useT } from '../../l10n/i18n';
import { errorMessage, nameDative } from '../core/presentation';
import { EmployeeAvatar } from '../core/widgets/EmployeeAvatar';
import { Sheet } from '../core/widgets/Sheet';

// The mobile app's sheets and dialogs (sheets.dart). Each is mounted while it is open, so it starts
// empty every time.

/** Cmd/Ctrl+Enter sends a multi-line field's form; Enter alone is a new line. */
function submitOnModEnter(e: KeyboardEvent<HTMLTextAreaElement>) {
  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
    e.preventDefault();
    e.currentTarget.form?.requestSubmit();
  }
}

/** Asks for a comment: what to change (required), why it is rejected, or where it was published. */
export function CommentSheet({
  title,
  hint,
  submitLabel,
  onSubmit,
  onClose,
  suggestions = [],
  required = false,
  danger = false,
  singleLine = false,
  inputType,
  initialText = '',
}: {
  title: string;
  hint: string;
  submitLabel: string;
  /** The text, trimmed ('' when left empty). */
  onSubmit: (text: string) => void;
  onClose: () => void;
  suggestions?: string[];
  required?: boolean;
  danger?: boolean;
  singleLine?: boolean;
  inputType?: 'url';
  /** What the field starts with (a suggestion the person may change or send as it is). */
  initialText?: string;
}) {
  const t = useT();
  const id = useId();
  const [text, setText] = useState(initialText);
  const canSubmit = !required || text.trim().length > 0;
  const addSuggestion = (s: string) => {
    const current = text.trim();
    setText(current.length === 0 ? s : `${current}. ${s}`);
  };
  return (
    <Sheet
      open
      onClose={onClose}
      title={title}
      labelledBy={`${id}-title`}
      onSubmit={() => canSubmit && onSubmit(text.trim())}
      actions={
        <>
          <button type="button" className="btn btn-text" onClick={onClose}>
            {t('actionCancel')}
          </button>
          <button type="submit" className={danger ? 'btn iv-btn-danger' : 'btn btn-filled'} disabled={!canSubmit}>
            {submitLabel}
          </button>
        </>
      }
    >
      {singleLine ? (
        <input
          className="input"
          type={inputType ?? 'text'}
          inputMode={inputType}
          value={text}
          placeholder={hint}
          aria-label={hint}
          autoComplete="off"
          onChange={(e) => setText(e.target.value)}
        />
      ) : (
        <textarea
          className="textarea"
          rows={3}
          value={text}
          placeholder={hint}
          aria-label={hint}
          aria-required={required}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={submitOnModEnter}
        />
      )}
      {suggestions.length > 0 && (
        <div className="iv-chips">
          {suggestions.map((s) => (
            <button key={s} type="button" className="iv-chip" onClick={() => addSuggestion(s)}>
              {s}
            </button>
          ))}
        </div>
      )}
    </Sheet>
  );
}

/** Asks before something leaves the organization: a message to a client, or a secret released. The
 * browser has no biometrics, so this click is the confirmation (ADR 0008). Cancel comes first, so
 * Enter on an untouched dialog sends nothing. */
export function ConfirmDialog({
  icon: Icon,
  tone,
  title,
  body,
  confirmLabel,
  onConfirm,
  onClose,
}: {
  icon: LucideIcon;
  tone: 'accent' | 'warning';
  title: string;
  body: string;
  confirmLabel: string;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const t = useT();
  const id = useId();
  return (
    <Sheet
      open
      onClose={onClose}
      labelledBy={`${id}-title`}
      actions={
        <>
          <button type="button" className="btn btn-text" onClick={onClose}>
            {t('actionCancel')}
          </button>
          <button type="button" className="btn btn-filled" onClick={onConfirm}>
            {confirmLabel}
          </button>
        </>
      }
    >
      <div className="iv-confirm">
        <span className={`iv-confirm-icon ${tone}`}>
          <Icon size={26} aria-hidden />
        </span>
        <h2 id={`${id}-title`} className="headline-small">
          {title}
        </h2>
        <p className="body-medium">{body}</p>
      </div>
    </Sheet>
  );
}

export function ConfirmSendDialog({ client, onConfirm, onClose }: { client: string; onConfirm: () => void; onClose: () => void }) {
  const t = useT();
  return (
    <ConfirmDialog
      icon={Send}
      tone="accent"
      title={t('confirmSendTitle')}
      body={t('confirmSendBody', { client })}
      confirmLabel={t('actionSend')}
      onConfirm={onConfirm}
      onClose={onClose}
    />
  );
}

/** Approving an ad change spends money at once: say which campaign and what changes. */
export function ConfirmAdDialog({
  campaign,
  change,
  onConfirm,
  onClose,
}: {
  campaign: string;
  change: string;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const t = useT();
  return (
    <ConfirmDialog
      icon={Megaphone}
      tone="warning"
      title={t('confirmAdTitle')}
      body={t('confirmAdBody', { campaign, change })}
      confirmLabel={t('actionApprove')}
      onConfirm={onConfirm}
      onClose={onClose}
    />
  );
}

/** Approving a document without an addressee publishes it: name it. One with an addressee is sent
 * and confirmed like any other message to a client. */
export function ConfirmPublishDialog({ title, onConfirm, onClose }: { title: string; onConfirm: () => void; onClose: () => void }) {
  const t = useT();
  return (
    <ConfirmDialog
      icon={FileText}
      tone="accent"
      title={t('confirmPublishTitle')}
      body={t('confirmPublishBody', { title })}
      confirmLabel={t('actionPublish')}
      onConfirm={onConfirm}
      onClose={onClose}
    />
  );
}

export function ConfirmAccessDialog({
  name,
  resource,
  onConfirm,
  onClose,
}: {
  name: string;
  resource: string;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const t = useT();
  return (
    <ConfirmDialog
      icon={KeyRound}
      tone="warning"
      title={t('confirmAccessTitle')}
      body={t('confirmAccessBody', { name, resource })}
      confirmLabel={t('actionAllowUse')}
      onConfirm={onConfirm}
      onClose={onClose}
    />
  );
}

/** How a handoff ended: who was asked, and the new task or the one they already work on. */
export type HandoffOutcome =
  | { employee: Employee; task: Item }
  | { employee: Employee; existingItemId: string };

/** Hands the work to one of `targets`, with an optional note. The handoff runs in the sheet, so the
 * person sees it happen; failures other than "already on it" stay here to retry. */
export function HandoffSheet({
  itemId,
  targets,
  onDone,
  onClose,
}: {
  itemId: string;
  targets: Employee[];
  onDone: (o: HandoffOutcome) => void;
  onClose: () => void;
}) {
  const t = useT();
  const locale = useLocale();
  const id = useId();
  const handOff = useHandOff();
  const [toId, setToId] = useState(targets[0]?.id);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string>();
  const to = targets.find((e) => e.id === toId) ?? targets[0];
  const busy = handOff.isPending;
  if (!to) return null;

  const submit = async () => {
    if (busy) return;
    setError(undefined);
    try {
      const task = await handOff.mutateAsync({ itemId, employeeId: to.id, note });
      onDone({ employee: to, task });
    } catch (e) {
      const err = toAppError(e);
      if (err.kind === 'alreadyHandedOff' && err.existingItemId) onDone({ employee: to, existingItemId: err.existingItemId });
      else setError(errorMessage(t, err, to.name));
    }
  };

  return (
    <Sheet
      open
      onClose={() => !busy && onClose()}
      labelledBy={`${id}-name`}
      onSubmit={() => void submit()}
      actions={
        <>
          <button type="button" className="btn btn-text" onClick={onClose} disabled={busy}>
            {t('actionCancel')}
          </button>
          <button type="submit" className="btn btn-filled" disabled={busy}>
            {busy ? <span className="iv-spinner" aria-hidden /> : <Forward size={18} aria-hidden />}
            {t('handoffTo', { name: nameDative(locale, to.name) })}
          </button>
        </>
      }
    >
      <div className="iv-handoff">
        {targets.length > 1 && <p className="overline faint iv-center">{t('handoffPick')}</p>}
        <div className="iv-handoff-faces" role="group" aria-label={t('handoffPick')}>
          {targets.map((e) => (
            <button
              key={e.id}
              type="button"
              className={e.id === to.id ? 'iv-face selected' : 'iv-face'}
              style={{ '--glow': e.color } as CSSProperties}
              aria-pressed={e.id === to.id}
              aria-label={e.name}
              disabled={busy}
              onClick={() => setToId(e.id)}
            >
              <EmployeeAvatar avatar={e.avatar} color={e.color} size={76} animate={e.id === to.id} />
            </button>
          ))}
        </div>
        <div className="iv-center" key={to.id}>
          <h2 id={`${id}-name`} className="headline-small">
            {to.name}
          </h2>
          <p className="body-medium muted">{to.title}</p>
          <p className="body-small iv-handoff-body">{t('handoffBody', { name: to.name })}</p>
        </div>
        <textarea
          className="textarea"
          rows={2}
          maxLength={4000}
          value={note}
          placeholder={t('handoffNoteHint')}
          aria-label={t('handoffNoteHint')}
          disabled={busy}
          onChange={(e) => setNote(e.target.value)}
          onKeyDown={submitOnModEnter}
        />
        {error && (
          <p className="iv-error body-small" role="alert">
            <CircleAlert size={18} aria-hidden />
            {error}
          </p>
        )}
      </div>
    </Sheet>
  );
}
